// React
import { Dispatch, FunctionComponent, SetStateAction, useState, useReducer, useEffect } from "react";
// Component
import { Title, FhirStatus, StatusAlert, StatusIcon } from '@fyrstain/fhir-front-library';
import { Fixture } from '../FixturesSection/FixturesSection';
// React Bootstrap
import { Form, Accordion, Button, InputGroup } from 'react-bootstrap';
// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
// Translation
import i18n from 'i18next';

///////////////////////////////////////
//             Interfaces             //
///////////////////////////////////////

interface ProfileSectionConfiguration {
    variables: Variable[],
    setVariables: Dispatch<SetStateAction<Variable[]>>,
    isVariableUsed: (variableId: string) => boolean,
    fixtures: Fixture[],
}

///////////////////////////////////////
//                Type               //
///////////////////////////////////////

export type Variable = {
    // The variable id to be unique in the page
    internalId: string,
    // The variable name to be unique in the TestScript 
    name: string;
    sourceId: string;
    expression: string;
    path: string;
};

const VariablesSection: FunctionComponent<ProfileSectionConfiguration> = (configs) => {

    /////////////////////////////////////
    //             Tooltip             //
    /////////////////////////////////////

    // TODO Double check
    const autocreateTooltip = i18n.t('tooltip.autocreate');
    const autodeleteTooltip = i18n.t('tooltip.autodelete');

    /////////////////////////////////////
    //              States             //
    /////////////////////////////////////

    const [editionValidated, setEditionValidated] = useState(false);
    // TODO initiate when edition
    const [counter, incrementCounter] = useReducer(x => x + 1, 0);
    const [editedVariable, setEditedVariable] = useState<Variable | undefined>(undefined);
    const emptyVariable = { name: '', sourceId: '', expression: '', path: '' } as Variable;

    // Error messages for General information fields
    const [messages, setMessages] = useState<{ [key: string]: string }>({
        name: i18n.t('errormessage.requiredfield'),
        sourceId: i18n.t('errormessage.requiredfield'),
        expression: i18n.t('errormessage.requiredfield'),
        path: i18n.t('errormessage.requiredfield')
    });

    // Check if the name field is touched
    const [isNameTouched, setNameTouched] = useState(false);

    /////////////////////////////////////
    //            Functions            //
    /////////////////////////////////////

    /**
     * Handle the change of the input fields for the edited variable
     * 
     * @param e The change event triggered by the form element.
     * 
     */
    const handleEditedVariableChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const validity = e.target.validity;
        if (name === 'name') {
            setNameTouched(true);
        }
        // TODO display the error message 
        if (validity.valueMissing) {
            setMessages({ ...messages, [name]: i18n.t('errormessage.requiredfield') });
        } else {
            setMessages({ ...messages, [name]: '' });
        }
        if (name === 'name') {
            if (editedVariable && configs.variables.find(variable => variable.name === value && variable.internalId !== editedVariable.internalId)) {
                setMessages({ ...messages, name: i18n.t('errormessage.variablenamealreadyexists') });
            } else {
                setMessages({ ...messages, name: '' });
            }
        }
        setEditedVariable(prevState => ({
            ...prevState,
            [name]: value
        } as Variable));
    };

    /**
     * Add a new variable to the table of variables
     *
     */
    const addVariable = async () => {
        if (editedVariable) {
            if (configs.variables.find(variable => variable.name === editedVariable.name && variable.internalId !== editedVariable.internalId)) {
                setMessages({ ...messages, name: i18n.t('errormessage.variablenamealreadyexists') });
                setEditionValidated(true);
                return;
            }
            if (editedVariable.name === ''
                || editedVariable.sourceId === ''
                || (editedVariable.path === '' && editedVariable.expression === '')) {
                setMessages({
                    ...messages,
                    name: i18n.t('errormessage.requiredfield'),
                    sourceId: i18n.t('errormessage.requiredfield'),
                    expression: !editedVariable.expression && !editedVariable.path ? i18n.t('errormessage.requiredfield') : '',
                    path: !editedVariable.path && !editedVariable.expression ? i18n.t('errormessage.requiredfield') : ''
                });
                setEditionValidated(true);
                return;
            }
            if (configs.variables.find(variable => variable.internalId === editedVariable.internalId)) {
                configs.setVariables(configs.variables.map(variable => {
                    if (variable.internalId === editedVariable.internalId) {
                        return editedVariable;
                    }
                    return variable;
                }))
            } else {
                configs.setVariables(prevVariable => [
                    ...prevVariable,
                    { ...editedVariable }
                ]);
            }
            setMessages({
                ...messages,
                name: '',
                sourceId: '',
                expression: '',
                path: ''
            });
            setEditedVariable(undefined);
            setEditionValidated(false);
        }
    };

    /**
     * Reset the variable form
     * 
     */
    const cancelVariable = () => {
        const confirmCancel = window.confirm(i18n.t('confirmationmessage.cancelvariable'));
        if (!confirmCancel) {
            return;
        }
        setEditedVariable(undefined);
        setEditionValidated(false);
        setMessages({
            ...messages,
            name: '',
            sourceId: '',
            expression: '',
            path: ''
        });
    };

    /**
     * Handle the form to edit the varaible from the edit icon
     * 
     * @param event 
     * 
     */
    const handleVariableEditingClick = (variable: Variable) => {
        setEditedVariable({ ...variable });
    };

    /**
     * Handle the delete of a variable from the faTrash icon to display a confirmation message
     * 
     * @param variableId 
     */
    const handleDeleteVariable = (variableId: string) => {
        if (configs.isVariableUsed(variableId)) {
            alert(i18n.t('errormessage.variableusedbyoperation'));
            return;
        }
        const confirmDelete = window.confirm(i18n.t('confirmationmessage.deletevariable'));
        if (confirmDelete) {
            configs.setVariables(prevVariables => prevVariables.filter(variable => variable.internalId !== variableId));
        }
    };

    /**
     * Edit a new variable
     * 
     */
    const editNewVariable = () => {
        incrementCounter();
        setEditedVariable({ ...emptyVariable, internalId: `variable-${counter}` });
    }

    /////////////////////////////////////
    //              Effects            //
    /////////////////////////////////////

    /**
     * Update the variables when the fixtures are updated
     * 
     */
    useEffect(() => {
        const updatedVariables = configs.variables.map(variable => {
            const fixture = configs.fixtures.find(fixture => fixture.id === variable.sourceId);
            if (fixture) {
                return { ...variable, sourceId: fixture.id };
            }
            return variable;
        });
        configs.setVariables(updatedVariables);
    }, [configs.fixtures]);

    /////////////////////////////////////
    //            Content              //
    /////////////////////////////////////

    return <Accordion
        defaultActiveKey="0"
    >
        <Accordion.Item eventKey="0">
            <Accordion.Header>
                <Title
                    level={2}
                    content='Variables'
                />
            </Accordion.Header>
            <Accordion.Body>
                {(configs.variables.length === 0 && !editedVariable) &&
                    <StatusAlert
                        status={FhirStatus.information}
                    >
                        <div className="d-flex align-items-center gap-2">
                            <StatusIcon status={FhirStatus.information} />
                            <strong>
                                {i18n.t('infomessage.novariable')}
                            </strong>
                        </div>
                    </StatusAlert>
                }
                <div className="d-flex flex-column gap-2">
                    <Accordion>
                        {configs.variables.map((variable, index) => (
                            variable.internalId !== editedVariable?.internalId &&

                            <Accordion.Item eventKey={index.toString()}>
                                <Accordion.Header>
                                    <div className="d-flex w-100 justify-content-between">
                                        <Title
                                            level={3}
                                            content={variable.name}
                                        />
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <FontAwesomeIcon
                                                icon={faPen}
                                                className="actionIcon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleVariableEditingClick(variable);
                                                }}
                                            />
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                className="actionIcon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteVariable(variable.internalId);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="d-flex flex-wrap gap-2">
                                    <div className="md-flex-container">
                                        <Form.Label className="fw-bold">
                                            SourceId :&nbsp;
                                        </Form.Label>
                                        <Form.Text>
                                            {configs.fixtures.find(fixture => fixture.internalId === variable.sourceId)?.id ?? variable.sourceId}
                                        </Form.Text>
                                    </div>
                                    {variable.path && (
                                        <div className="md-flex-container">
                                            <Form.Label className="fw-bold">
                                                {i18n.t('label.path')} :&nbsp;
                                            </Form.Label>
                                            <Form.Text>
                                                {variable.path}
                                            </Form.Text>
                                        </div>
                                    )}
                                    {variable.expression && (
                                        <div className="md-flex-container">
                                            <Form.Label className="fw-bold">
                                                Expression :&nbsp;
                                            </Form.Label>
                                            <Form.Text>
                                                {variable.expression}
                                            </Form.Text>
                                        </div>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>
                        ))
                        }
                    </Accordion>

                    {/* EDITED VARIABLE FORM */}
                    {editedVariable &&
                        <div className={`d-flex flex-wrap gap-4 ${editionValidated ? 'was-validated' : ''}`}>
                            <div className="md-flex-container">
                                <Form.Group className="md-flex-container">
                                    <Form.Label className="fw-bold">
                                        {i18n.t('label.name')} *
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            name="name"
                                            required
                                            value={editedVariable.name}
                                            onChange={handleEditedVariableChange}
                                            isInvalid={isNameTouched && !!messages.name}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback
                                        type="invalid"
                                        className={isNameTouched ? 'd-block' : 'd-none'}
                                    >
                                        {messages.name}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="md-flex-container">
                                <Form.Group className="md-flex-container">
                                    <Form.Label className="fw-bold">
                                        SourceId *
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Select
                                            name="sourceId"
                                            required
                                            value={editedVariable.sourceId}
                                            onChange={handleEditedVariableChange}
                                        >
                                            <option value="">{i18n.t('placeholder.sourceid')}</option>
                                            {configs.fixtures.map(fixture => (
                                                <option
                                                    key={fixture.internalId}
                                                    value={fixture.internalId}
                                                >
                                                    {fixture.id}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </InputGroup>
                                    <Form.Control.Feedback
                                        type="invalid"
                                    >
                                        {messages.sourceId}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="md-flex-container">
                                <Form.Group className="md-flex-container">
                                    <Form.Label className="fw-bold">
                                        {i18n.t('label.enteranexpression')} *
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            name="expression"
                                            required
                                            value={editedVariable.expression}
                                            onChange={handleEditedVariableChange}
                                            disabled={!!editedVariable.path}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback
                                        type="invalid"
                                    >
                                        {messages.expression}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="md-flex-container">
                                <Form.Group className="md-flex-container">
                                    <Form.Label className="fw-bold">
                                        {i18n.t('label.enterapath')} *
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            name="path"
                                            required
                                            value={editedVariable.path}
                                            onChange={handleEditedVariableChange}
                                            disabled={!!editedVariable.expression}
                                        />
                                    </InputGroup>
                                    <Form.Control.Feedback
                                        type="invalid"
                                    >
                                        {messages.path}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <Button
                                className="w-9rem"
                                role="button"
                                variant="primary"
                                onClick={addVariable}
                            >
                                {i18n.t('button.add')}
                            </Button>
                            <Button
                                className="w-9rem"
                                role="button"
                                variant="secondary"
                                onClick={cancelVariable}
                            >
                                {i18n.t('button.cancel')}
                            </Button>
                        </div>
                    }
                    {/* END VARIABLE FORM */}

                    {!editedVariable &&
                        <div className="actionIcon d-flex flex-wrap align-items-center gap-2 w-15rem">
                            <FontAwesomeIcon
                                icon={faPlus}
                                onClick={() => editNewVariable()}
                                role="button"
                            />
                            <strong
                                onClick={() => editNewVariable()}
                                role="button"
                            >
                                {i18n.t('button.addvariable')}
                            </strong>
                        </div>
                    }
                </div>
            </Accordion.Body>
        </Accordion.Item>
    </Accordion >
};

export default VariablesSection;